import PropTypes from "prop-types";
import React from "react";

import { connect } from "react-redux";

import { Link, useLocation } from "react-router-dom";

// Custom Components
import NotificationDropdown from "../Common/NotificationDropdown";
import favoriteChartBlack from "../../assets/images/restaurant-portal/favorite-chart-black.svg";
import favoriteChartOrange from "../../assets/images/restaurant-portal/favorite-chart-orange.svg";
import coinBlack from "../../assets/images/restaurant-portal/coin-black.svg";
import coinOrange from "../../assets/images/restaurant-portal/coin-orange.svg";
import profile from "../../assets/images/user-dashboard/profile.svg";
import profileOrange from "../../assets/images/user-dashboard/profile-orange.svg";
import logoDashboard from "../../assets/images/user-dashboard/logo-dashboard.svg";

//Import API
import endpoints from "../../api/endpoints";
import { get, patch } from "../../api/axios";

//i18n
import { withTranslation } from "react-i18next";

// Redux Store
import { showRightSidebarAction, toggleLeftmenu } from "../../store/actions";

const AdminHeader = (props) => {
  const location = useLocation();
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const isActiveRoute = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // Fetch notifications
  const fetchNotifications = React.useCallback(async () => {
    try {
      const response = await get(`${endpoints.adminNotificationsUnread}`);
      if (response.data.success) {
        const notificationsData = response.data.data.data || [];
        const transformedNotifications = notificationsData.map((notif) => ({
          id: notif.id,
          title: notif.notification_type?.replace(/_/g, " ") || "Notification",
          message: notif.data?.message || notif.message || "",
          type: notif.notification_type || "system",
          read: notif.is_read || false,
          timeAgo: formatTimeAgo(notif.created_at),
          url: notif.data?.url || notif.data?.redirect_url || null,
        }));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  // Fetch unread count separately
  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const response = await get(endpoints.adminNotificationsUnreadCount);
      if (response.data.success) {
        const count = response.data.data?.unread_count || 0;
        setUnreadCount(Number(count));
      }
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch notifications on mount and set up polling
  React.useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();

    // Poll for new notifications every 10 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchUnreadCount();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      if (notificationId === "all") {
        // Mark all as read using the read-all endpoint
        await patch(endpoints.adminNotificationsReadAll);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        fetchUnreadCount();
      } else {
        const endpoint = endpoints.adminNotificationMarkRead.replace(
          "{{NOTIFICATION_ID}}",
          notificationId,
        );
        await patch(endpoint);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
        );
        fetchUnreadCount();
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  return (
    <React.Fragment>
      {props.leftMenu && (
        <div
          className="sidebar-overlay"
          onClick={() => props.toggleLeftmenu(false)}
          aria-label="Close sidebar"
        />
      )}
      <header id="page-topbar">
        <div className="new-header-container">
          {/* Left side - Hamburger button and Logo */}
          <div className="header-left">
            <button
              type="button"
              onClick={() => props.toggleLeftmenu(!props.leftMenu)}
              className="hamburger-menu-btn"
              aria-label="Toggle sidebar"
            >
              <i className="fa fa-bars" />
            </button>
            <Link to="/admin/dashboard" className="brand-logo">
              <img
                src={logoDashboard}
                alt="OMAHA Food Lovers"
                className="brand-logo-image"
              />
            </Link>
          </div>

          {/* Middle - Navigation Tabs */}
          <div className="header-center">
            <nav className="nav-tabs">
              <Link
                to="/admin/dashboard"
                className={`nav-tab ${isActiveRoute("/admin/dashboard") ? "active" : ""}`}
              >
                <img
                  src={
                    isActiveRoute("/admin/dashboard")
                      ? favoriteChartOrange
                      : favoriteChartBlack
                  }
                  alt="Home"
                  className="nav-tab-icon"
                />
                <span>Home</span>
              </Link>

              <Link
                to="/admin/settlements"
                className={`nav-tab ${isActiveRoute("/admin/settlements") ? "active" : ""}`}
              >
                <img
                  src={
                    isActiveRoute("/admin/settlements") ? coinOrange : coinBlack
                  }
                  alt="Settlements"
                  className="nav-tab-icon"
                />
                <span>Settlements</span>
              </Link>
              <Link
                to="/admin/profile"
                className={`nav-tab ${isActiveRoute("/admin/profile") ? "active" : ""}`}
              >
                <img
                  src={
                    isActiveRoute("/admin/profile") ? profileOrange : profile
                  }
                  alt="Profile"
                  className="nav-tab-icon"
                />
                <span>Profile</span>
              </Link>
              <div className="nav-tab notification-tab desktop-only">
                <NotificationDropdown
                  notifications={notifications}
                  unreadCount={unreadCount}
                  onMarkAsRead={handleMarkAsRead}
                  onOpen={fetchNotifications}
                  viewAllPath="/admin/notifications"
                />
              </div>
            </nav>
          </div>

          <div className="header-right"></div>
        </div>
      </header>
    </React.Fragment>
  );
};

AdminHeader.propTypes = {
  leftMenu: PropTypes.bool,
  showRightSidebar: PropTypes.bool,
  toggleLeftmenu: PropTypes.func,
  showRightSidebarAction: PropTypes.func,
};

const mapStatetoProps = (state) => {
  const { leftMenu, showRightSidebar } = state.Layout;
  return { leftMenu, showRightSidebar };
};

export default connect(mapStatetoProps, {
  toggleLeftmenu,
  showRightSidebarAction,
})(withTranslation()(AdminHeader));
